import { clearAuthAndRedirect, getSessionToken, shouldHandleAuthRedirect } from './auth.js';

class HttpError extends Error {
  constructor(message, { status, response, payload } = {}) {
    super(message);
    this.name = 'HttpError';
    this.status = status ?? 0;
    this.response = response ?? null;
    this.payload = payload ?? null;
    this.data = payload ?? null;
  }
}

const isFormData = (value) => typeof FormData !== 'undefined' && value instanceof FormData;

const mergeHeaders = (...headerSets) => {
  const mergedHeaders = new Headers();

  headerSets.filter(Boolean).forEach((headerSet) => {
    new Headers(headerSet).forEach((value, key) => {
      mergedHeaders.set(key, value);
    });
  });

  return mergedHeaders;
};

const buildRequestInit = (options = {}) => {
  const {
    auth = true,
    data,
    headers,
    parseAs,
    skipAuthRedirect,
    ...fetchOptions
  } = options;

  const requestHeaders = mergeHeaders(headers);
  const token = getSessionToken();
  const method = (fetchOptions.method || 'GET').toUpperCase();

  if (auth && token && !requestHeaders.has('Authorization')) {
    requestHeaders.set('Authorization', `Bearer ${token}`);
  }

  if (data !== undefined) {
    if (!requestHeaders.has('Content-Type')) {
      requestHeaders.set('Content-Type', 'application/json');
    }

    fetchOptions.body = JSON.stringify(data);
  }

  if (fetchOptions.body && !isFormData(fetchOptions.body) && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  return {
    auth,
    parseAs,
    skipAuthRedirect,
    init: {
      ...fetchOptions,
      __skipGlobalAuthRedirect: true,
      method,
      headers: requestHeaders
    }
  };
};

const parseTextSafely = async (response) => {
  try {
    return await response.text();
  } catch {
    return '';
  }
};

const parseJsonSafely = (text) => {
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const parseResponseBody = async (response, parseAs = 'auto') => {
  if (parseAs === 'raw') {
    return response;
  }

  if (parseAs === 'blob') {
    return response.blob();
  }

  if (parseAs === 'text') {
    return parseTextSafely(response);
  }

  if (response.status === 204 || response.status === 205) {
    return null;
  }

  const responseText = await parseTextSafely(response);
  const contentType = response.headers.get('content-type') || '';
  const jsonPayload = parseJsonSafely(responseText);

  if (parseAs === 'json') {
    return jsonPayload;
  }

  if (contentType.includes('application/json')) {
    return jsonPayload;
  }

  return jsonPayload ?? responseText;
};

const resolveErrorMessage = (response, payload) => {
  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim();
  }

  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message.trim();
    }

    if (typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error.trim();
    }
  }

  return `HTTP error! status: ${response.status}`;
};

const request = async (url, options = {}) => {
  const { auth, init, parseAs, skipAuthRedirect } = buildRequestInit(options);
  const response = await fetch(url, init);
  const isAuthRedirectSkipped = Boolean(skipAuthRedirect || init?.__skipGlobalAuthRedirect);

  if (
    auth
    && !isAuthRedirectSkipped
    && (response.status === 401 || response.status === 403)
    && shouldHandleAuthRedirect(url)
  ) {
    clearAuthAndRedirect();
  }

  if (parseAs === 'raw' && response.ok) {
    return response;
  }

  const payload = await parseResponseBody(response, parseAs);

  if (!response.ok) {
    throw new HttpError(resolveErrorMessage(response, payload), {
      status: response.status,
      response,
      payload
    });
  }

  return payload;
};

export const httpClient = {
  request,
  get: (url, options = {}) => request(url, { ...options, method: 'GET' }),
  post: (url, options = {}) => request(url, { ...options, method: 'POST' }),
  put: (url, options = {}) => request(url, { ...options, method: 'PUT' }),
  patch: (url, options = {}) => request(url, { ...options, method: 'PATCH' }),
  delete: (url, options = {}) => request(url, { ...options, method: 'DELETE' })
};

export { HttpError };
